import { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CircleStackIcon,
  CloudArrowUpIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { Modal, ConfirmDialog, PageLoader, EmptyState, StatusBadge } from '@/components/common';
import { databasesApi } from '@/services/api';
import type { DatabaseInfo, Backup } from '@/types';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(date: string): string {
  return new Date(date).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DatabasesPage() {
  const [loading, setLoading] = useState(true);
  const [databases, setDatabases] = useState<DatabaseInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [selectedDb, setSelectedDb] = useState<DatabaseInfo | null>(null);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [showBackupsModal, setShowBackupsModal] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<Backup | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadDatabases();
  }, [page]);

  const loadDatabases = async () => {
    try {
      const response = await databasesApi.list(page, limit);
      setDatabases(response.data.databases || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Error al cargar bases de datos');
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async (orgId: string) => {
    try {
      const response = await databasesApi.listBackups(orgId);
      setBackups(response.data.backups || []);
    } catch (error) {
      toast.error('Error al cargar backups');
    }
  };

  const handleCreateBackup = async (db: DatabaseInfo) => {
    setActionLoading(true);
    try {
      const response = await databasesApi.backup(db.organization.id);
      toast.success(`Backup creado: ${response.data.backup.name}`);

      // If backups modal is open, reload backups
      if (showBackupsModal && selectedDb?.organization.id === db.organization.id) {
        loadBackups(db.organization.id);
      }
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al crear backup';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadBackup = async (backup: Backup) => {
    if (!selectedDb) return;

    try {
      const response = await databasesApi.downloadBackup(selectedDb.organization.id, backup.name);
      const blob = new Blob([response.data], { type: 'application/gzip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backup.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Descarga iniciada');
    } catch (error) {
      toast.error('Error al descargar backup');
    }
  };

  const handleRestore = async () => {
    if (!selectedDb || !selectedBackup) return;

    setActionLoading(true);
    try {
      await databasesApi.restore(selectedDb.organization.id, selectedBackup.name);
      toast.success('Base de datos restaurada');
      setShowRestoreConfirm(false);
      setSelectedBackup(null);
      loadDatabases();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al restaurar';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const openBackupsModal = async (db: DatabaseInfo) => {
    setSelectedDb(db);
    setShowBackupsModal(true);
    await loadBackups(db.organization.id);
  };

  if (loading) {
    return <PageLoader />;
  }

  const totalDocs = databases.reduce((acc, db) => acc + (db.stats?.documents || 0), 0);
  const totalSize = databases.reduce((acc, db) => acc + (db.stats?.size_bytes || 0), 0);

  return (
    <div>
      <Header
        title="Bases de Datos"
        subtitle="Gestiona las bases de datos de todas las organizaciones"
      />

      <div className="p-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-vida-100 rounded-lg">
                <CircleStackIcon className="w-5 h-5 text-vida-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total BBDDs</p>
                <p className="text-xl font-bold">{databases.length}</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DocumentDuplicateIcon className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Documentos</p>
                <p className="text-xl font-bold">{totalDocs.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <CloudArrowUpIcon className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Almacenamiento</p>
                <p className="text-xl font-bold">{formatBytes(totalSize)}</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ArrowPathIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Activas</p>
                <p className="text-xl font-bold">
                  {databases.filter((db) => db.organization.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Databases table */}
        {databases.length === 0 ? (
          <EmptyState
            icon={CircleStackIcon}
            title="No hay bases de datos"
            description="Crea una organización para generar su base de datos"
          />
        ) : (
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Organización</th>
                    <th>Base de datos</th>
                    <th>Colecciones</th>
                    <th>Documentos</th>
                    <th>Tamaño</th>
                    <th>Estado</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {databases.map((db) => (
                    <tr key={db.database_name}>
                      <td>
                        <div>
                          <p className="font-medium text-gray-900">{db.organization.name}</p>
                          <p className="text-sm text-gray-500">{db.organization.slug}</p>
                        </div>
                      </td>
                      <td className="font-mono text-sm text-gray-500">{db.database_name}</td>
                      <td>{db.stats?.collections || '-'}</td>
                      <td>{db.stats?.documents?.toLocaleString() || '-'}</td>
                      <td>{db.stats ? formatBytes(db.stats.size_bytes) : '-'}</td>
                      <td>
                        <StatusBadge status={db.organization.status} />
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openBackupsModal(db)}
                            className="p-1 text-gray-400 hover:text-vida-600"
                            title="Ver backups"
                          >
                            <CloudArrowUpIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleCreateBackup(db)}
                            disabled={actionLoading}
                            className="p-1 text-gray-400 hover:text-green-600"
                            title="Crear backup"
                          >
                            <ArrowDownTrayIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > limit && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(page - 1) * limit + 1} - {Math.min(page * limit, total)} de {total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn-secondary text-sm"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * limit >= total}
                    className="btn-secondary text-sm"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Backups modal */}
      <Modal
        open={showBackupsModal}
        onClose={() => {
          setShowBackupsModal(false);
          setSelectedDb(null);
        }}
        title={`Backups - ${selectedDb?.organization.name}`}
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              Base de datos: <code className="bg-gray-100 px-2 py-1 rounded">{selectedDb?.database_name}</code>
            </p>
            <button
              onClick={() => selectedDb && handleCreateBackup(selectedDb)}
              disabled={actionLoading}
              className="btn-primary"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Crear backup
            </button>
          </div>

          {backups.length === 0 ? (
            <EmptyState
              icon={CloudArrowUpIcon}
              title="No hay backups"
              description="Crea el primer backup de esta base de datos"
            />
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {backups.map((backup) => (
                <div
                  key={backup.name}
                  className="p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900 font-mono text-sm">{backup.name}</p>
                    <p className="text-sm text-gray-500">
                      {formatDate(backup.created_at)} · {formatBytes(backup.size_bytes)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDownloadBackup(backup)}
                      className="btn-secondary text-sm py-1"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                      Descargar
                    </button>
                    <button
                      onClick={() => {
                        setSelectedBackup(backup);
                        setShowRestoreConfirm(true);
                      }}
                      className="btn text-sm py-1 bg-amber-100 text-amber-700 hover:bg-amber-200"
                    >
                      <ArrowPathIcon className="w-4 h-4 mr-1" />
                      Restaurar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Restore confirmation */}
      <ConfirmDialog
        open={showRestoreConfirm}
        onClose={() => {
          setShowRestoreConfirm(false);
          setSelectedBackup(null);
        }}
        onConfirm={handleRestore}
        title="Restaurar base de datos"
        message={`¿Estás seguro de restaurar la base de datos "${selectedDb?.database_name}" desde el backup "${selectedBackup?.name}"? Los datos actuales serán reemplazados.`}
        confirmText="Restaurar"
        variant="warning"
        loading={actionLoading}
      />
    </div>
  );
}
